export function fmt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** Единица только для отображения: «м» у ткани (по имени), иначе «шт.». Роль/склады тут ни при чём. */
export function unitOf(name) {
  return /ткань/i.test(String(name || '')) ? 'м' : 'шт.';
}

/** Подпись области действия техкарты по plan_scope сервера. */
export function scopeLabel(scope) {
  if (scope === 'family') return 'на всю семью';
  if (scope === 'variant') return 'только эта модификация';
  if (scope === 'members') return 'на модификации этой ветки';
  return '';
}

/** Тост после PUT /tech-chain/plan по ответу сервера. */
export function planSavedMessage(planId, scope) {
  if (!planId) return 'Выбор техкарты снят';
  if (scope === 'family') return 'Техкарта выбрана на всю семью';
  if (scope === 'variant') return 'Техкарта выбрана только для этой модификации';
  if (scope === 'members') return 'Техкарта выбрана для модификаций этой ветки';
  return 'Техкарта выбрана';
}

/** member_ids для PUT /plan: модификации ветки, когда слот объединяет несколько. */
export function planMemberIds(node) {
  const members = Array.isArray(node?.members) ? node.members : [];
  return members.length > 1 ? members : undefined;
}

export function modsLabel(count) {
  const n = Number(count) || 0;
  const n10 = n % 10;
  const n100 = n % 100;
  let word = 'модификаций';
  if (n10 === 1 && n100 !== 11) word = 'модификация';
  else if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) word = 'модификации';
  return `${n} ${word}`;
}

export function sameId(a, b) {
  return String(a) === String(b);
}

function idEq(a, b) {
  return String(a) === String(b);
}

function hasId(list, key) {
  return Array.isArray(list) && list.some((id) => idEq(id, key));
}

function sameSet(a, b) {
  const left = new Set((a || []).map((id) => String(id)));
  const right = new Set((b || []).map((id) => String(id)));
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

export function normalizeView(data) {
  const rules = data?.rules && typeof data.rules === 'object' && !Array.isArray(data.rules)
    ? data.rules
    : {};
  return {
    parent: data?.parent ?? null,
    stores: data?.stores ?? { product: [], semi: [], fabric: [] },
    rules,
    tree: Array.isArray(data?.tree) ? data.tree : [],
    children: Array.isArray(data?.children) ? data.children : [],
    issues: Array.isArray(data?.issues) ? data.issues : [],
  };
}

/** Сохранённые массивы копируем в черновик. null остаётся «правило не задано» до первого клика. */
export function draftFromRules(rules) {
  const draft = {};
  if (!rules || typeof rules !== 'object') return draft;
  for (const [ownerId, slots] of Object.entries(rules)) {
    if (Array.isArray(slots)) draft[ownerId] = [...slots];
  }
  return draft;
}

export function toggleSlot(draftRules, savedRules, ownerId, key) {
  const current = Array.isArray(draftRules?.[ownerId])
    ? draftRules[ownerId]
    : (Array.isArray(savedRules?.[ownerId]) ? [...savedRules[ownerId]] : []);
  const nextSlots = hasId(current, key)
    ? current.filter((id) => !idEq(id, key))
    : [...current, key];
  return { ...draftRules, [ownerId]: nextSlots };
}

/** Только владельцы, чьё правило изменилось или было null и теперь задаётся. */
export function changedRules(savedRules, draftRules) {
  const out = {};
  for (const [ownerId, slots] of Object.entries(draftRules || {})) {
    if (!Array.isArray(slots)) continue;
    const saved = savedRules?.[ownerId];
    if (!Array.isArray(saved) || !sameSet(saved, slots)) out[ownerId] = [...slots];
  }
  return out;
}

export function isSlotIncluded(savedRules, draftRules, ownerId, key) {
  const draft = draftRules?.[ownerId];
  if (Array.isArray(draft)) return hasId(draft, key);
  const saved = savedRules?.[ownerId];
  if (Array.isArray(saved)) return hasId(saved, key);
  return false;
}

export function slotDirty(savedRules, draftRules, ownerId, key) {
  const draft = draftRules?.[ownerId];
  if (!Array.isArray(draft)) return false;
  const saved = savedRules?.[ownerId];
  const inSaved = hasId(saved, key);
  return inSaved !== hasId(draft, key);
}

function walkSlots(nodes, savedRules, draftRules, depth, ancestorExcluded, seen, out) {
  for (const node of nodes || []) {
    const ownerKey = node.owner_id == null ? null : String(node.owner_id);
    const included = isSlotIncluded(savedRules, draftRules, node.owner_id, node.key);
    const ruleSet = Array.isArray(draftRules?.[node.owner_id]) || Boolean(node.rule_set);
    const showRuleHint = !ruleSet && ownerKey != null && !seen.has(ownerKey);
    if (!ruleSet && ownerKey != null) seen.add(ownerKey);
    out.push({
      node,
      depth,
      included,
      notCounted: ancestorExcluded || !included,
      dirty: slotDirty(savedRules, draftRules, node.owner_id, node.key),
      showRuleHint,
    });
    walkSlots(
      node.children,
      savedRules,
      draftRules,
      depth + 1,
      ancestorExcluded || !included,
      seen,
      out,
    );
  }
  return out;
}

export function flattenSlots(nodes, savedRules, draftRules) {
  return walkSlots(nodes, savedRules, draftRules, 0, false, new Set(), []);
}

/** Куда PUT /plan: один член ветки — его ms_id, иначе ключ слота (параметрика родителя). */
export function planTargetId(node) {
  const members = Array.isArray(node?.members) ? node.members : [];
  return members.length === 1 ? members[0] : node?.key;
}

const CYR_SIZE = { Х: 'X', Л: 'L', М: 'M', С: 'S' };
const SIZE_RE = /^(X{0,4}S|M|X{0,4}L|[2-6]XL)$/;

/** Размер модификации из скобок имени: «5 Шайба Пуф (XL, 13 Серый, Kvest)» → «XL». */
export function sizeOf(name) {
  const groups = String(name || '').match(/\(([^()]*)\)/g) || [];
  for (const group of groups.reverse()) {
    for (const raw of group.slice(1, -1).split(',')) {
      const token = raw.trim().toUpperCase().replace(/[ХЛМС]/g, (ch) => CYR_SIZE[ch]);
      if (SIZE_RE.test(token)) return token;
    }
  }
  return null;
}

/** Размер из имени техкарты «Пошив чехла Шайба ХЛ» → «XL»: только токены с X, чтобы «с поролоном» не стало S. */
export function cardSizeOf(name) {
  for (const raw of String(name || '').split(/[\s,()/]+/)) {
    const token = raw.trim().toUpperCase().replace(/[ХЛМС]/g, (ch) => CYR_SIZE[ch]);
    if (/^(X{1,4}[SL]|[2-6]XL)$/.test(token)) return token;
  }
  return null;
}

/**
 * Карта, подходящая слоту по размеру из его имени («5 Шайба Пуф Чехол (XL)» → «Пошив чехла Шайба ХЛ»).
 * Только подсказка для выбора, ничего не сохраняет; null — размера нет или кандидатов не ровно один.
 */
export function suggestedCard(node) {
  const size = sizeOf(node?.name);
  if (!size) return null;
  const cards = Array.isArray(node?.cards) ? node.cards : [];
  if (cards.length < 2) return null;
  const matches = cards.filter((card) => cardSizeOf(card.name) === size);
  return matches.length === 1 ? matches[0] : null;
}

function sizeRank(size) {
  const n = /^(\d)XL$/.exec(size);
  if (n) return 30 + Number(n[1]);
  const xs = (size.match(/X/g) || []).length;
  if (size.endsWith('S')) return 10 - xs;
  if (size === 'M') return 20;
  return 30 + xs;
}

function bySize(a, b) {
  return sizeRank(a) - sizeRank(b);
}

export const COMMON_SIZE_LABEL = 'Общие для всех размеров';

/**
 * Верхние слоты по размерам модификаций, которые их используют (ChildRow.tree.key).
 * null — размеров меньше двух или разбивать нечего: дерево без заголовков.
 */
export function groupSlotsBySize(tree, children) {
  const slotSizes = new Map();
  const memberSizes = new Map();
  const allSizes = new Set();
  const addSize = (map, id, size) => {
    const key = String(id);
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(size);
  };
  for (const child of children || []) {
    const size = sizeOf(child?.name);
    if (!size) continue;
    allSizes.add(size);
    for (const item of child.tree || []) {
      addSize(slotSizes, item.key, size);
      if (item.id != null) addSize(memberSizes, item.id, size);
    }
  }
  if (allSizes.size < 2) return null;
  const groups = new Map();
  for (const node of tree || []) {
    // Слот с членами ветки — размеры модификаций, использующих именно этих членов
    // (чехол (XL) и чехол (XXL) делят один ключ); без членов — по ключу.
    const byMembers = new Set();
    for (const id of node.members || []) {
      for (const size of memberSizes.get(String(id)) || []) byMembers.add(size);
    }
    const sizes = [...(byMembers.size ? byMembers : (slotSizes.get(String(node.key)) || []))].sort(bySize);
    const common = !sizes.length || sizes.length === allSizes.size;
    const label = common ? COMMON_SIZE_LABEL : sizes.join(', ');
    if (!groups.has(label)) groups.set(label, { label, common, rank: common ? Infinity : sizeRank(sizes[0]), span: sizes.length, nodes: [] });
    groups.get(label).nodes.push(node);
  }
  const list = [...groups.values()];
  if (list.filter((g) => !g.common && g.span === 1).length < 2) return null;
  return list.sort((a, b) => a.rank - b.rank || a.span - b.span);
}

/** Строки дерева по группам; подсказка «Правило не задано» — один раз на owner для всех групп. */
export function flattenSlotGroups(groups, savedRules, draftRules) {
  const seen = new Set();
  return groups.map((group) => ({
    label: group.label,
    rows: walkSlots(group.nodes, savedRules, draftRules, 0, false, seen, []),
  }));
}

/**
 * Узкое место добавки по ChildRow.tree: учтённые слоты верхнего уровня с флагом limiting.
 * path — вглубь по limiting-детям до первопричины (чехол → крой → ткань).
 * null — в цепочке нет учтённых слотов.
 */
export function limitingOf(tree) {
  const top = (tree || []).filter((node) => node?.counted && node.limiting);
  if (!top.length) return null;
  const path = [];
  let node = top[0];
  while (node) {
    path.push(node);
    node = (node.children || []).find((child) => child?.counted && child.limiting) || null;
  }
  return { node: top[0], others: top.length - 1, path };
}

export function flattenChain(nodes, depth = 0, out = []) {
  for (const node of nodes || []) {
    out.push({ node, depth });
    flattenChain(node.children, depth + 1, out);
  }
  return out;
}
